// lib/access/sourceAccess.ts
//
// Who can use the Source tool, while it is a pilot.
//
// Deliberately the same shape as lib/access/slidesAccess.ts, because it is the same situation and
// two different answers to "who may use a pilot tool" is one too many. Access is a CLOSED
// allowlist of individual users: not an organisation, not a user activity (those are org-wide
// grants and would let Source in for every Altarea account at once), and not "any Fater admin".
//
// To grant access, add a User.id below and say who it is. Ids are stable; emails change.
// To open Source to everyone again, delete this module and drop the gate back to
// `canAccessSource` in the portal nav, do NOT quietly grow this list into a permission system.
//
// Two lists: the people BUILDING the tool, and the pilot users EVALUATING it. Both get in; only
// the pilot users are told so in the UI, because the builders do not need warning that it has bugs.

import type { UserContext } from '#types/types';
import { canEditAnalystWorkbook } from '#lib/access/analystAccess';

/**
 * PILOT accounts: outside Fater, here to use the tool on real deals rather than build it.
 *
 * Kept separate from the builders because the difference is visible, a pilot user gets the
 * "Limited Trial" pill in the nav and the welcome notice on entry.
 */
const SOURCE_PILOT_USER_IDS: ReadonlySet<string> = new Set([
  '68d266d774f75ae049a2dbfd', // Benoist BITOUNE, bbitoune@altarea.com (Altarea)
  '698b7d86f4b2602f85d8d18d', // Valentina Gacic, vgacic@altarea.com (Altarea)
  '68beba88c628b46023fd7246', // Romain Ducreux, rducreux@altarea.com (Altarea, Aubergenville)
]);

/** The only accounts that may use Source, by User.id. */
const SOURCE_ALLOWED_USER_IDS: ReadonlySet<string> = new Set([
  '665f5c236de32fb66dad6c81', // Jonathann Jacobs, jonathann@fater.ai
  '680f5aefb1c5d15b63103e4e', // Jonathann Jacobs, jonathannjvp@gmail.com
  '66146f123b47aef3810c55ba', // Youssef Guessous, yg@fater.ai
  '69769fa7169ae03c28659dd6', // Youssef Guessous, youssef@fater.ai (the test-Clerk identity; also on the Slides builder list)
  ...SOURCE_PILOT_USER_IDS,
]);

const normalizeUserId = (user: Pick<UserContext, 'dbUserId'> | null | undefined): string => {
  const raw = user?.dbUserId;
  return typeof raw === 'string' ? raw.trim() : '';
};

/** May this account open Source at all? */
export const canAccessSource = (user: Pick<UserContext, 'dbUserId'> | null | undefined): boolean => {
  const id = normalizeUserId(user);
  return id.length > 0 && SOURCE_ALLOWED_USER_IDS.has(id);
};

/**
 * Is this account here to EVALUATE Source rather than build it? Drives the "Limited Trial" pill
 * and the welcome notice, never access itself, which `canAccessSource` alone decides.
 */
export const isSourcePilotUser = (
  user: Pick<UserContext, 'dbUserId'> | null | undefined,
): boolean => {
  const id = normalizeUserId(user);
  return id.length > 0 && SOURCE_PILOT_USER_IDS.has(id);
};

export function canEditSourceRentRoll(
  user: Pick<UserContext, 'dbUserId' | 'isImpersonating'> & Partial<Pick<UserContext, 'isFaterAdmin'>>,
  assignedOwnerIds: readonly string[] | undefined,
): boolean {
  return user.isFaterAdmin === true
    ? Boolean(user.dbUserId && !user.isImpersonating)
    : canEditAnalystWorkbook(user, assignedOwnerIds);
}
